import Phaser from "phaser";
import i18next from "i18next";
import { ItemSlot } from "../item-slot";
import { COLORS, hexToPhaser } from "../colors";
import { BaseWindow } from "./base-window";
import { ModernButton } from "../modern-button";
import { IAPManager } from "../../managers/iap-manager";
import { IAPProduct } from "../../types/iap";
import { isTouchDevice } from "../../utils/mobile-utils";
import { playSound } from "../../utils/sound-utils";
import { NotificationHandler } from "../notification-handler";
import { trackEvent } from "../../utils/ga-events";
import { showConfetti } from "../splash-particle";

interface ServerIAPProduct {
  id: string;
  product_type: string;
  name: string;
  description: string;
  apple_product_id: string | null;
  google_product_id: string | null;
  amount: number;
  display_order: number;
  enabled: boolean;
}

interface CombinedProduct extends ServerIAPProduct {
  appleProductId?: string;
  googleProductId?: string;
  priceFormatted?: string;
}

type CosmeticTab = "profileIcons" | "hats" | "titles" | "borders";
type CatalogEntry = { id: number; name?: string; sprite?: string; text?: string };
type SeasonSkinCatalogEntry = { id: string; name?: string; sprite?: string };

// Sentinel id for the "None" hat tile — lets players unequip a hat. Real hats.json ids start at 1.
const NONE_HAT_ID = 0;
// Titles already use 0 to mean "none equipped" (see equippedTitle), so the same value
// doubles as the "None" tile's id — real titles.json ids start at 1.
const NONE_TITLE_ID = 0;
// Borders already use 0 to mean "none equipped" (see equippedBorder), same convention as titles.
const NONE_BORDER_ID = 0;

interface SeasonDataForCosmetics {
  rewards: { level: number; free: any; premium: any }[];
  cosmetics: {
    unlockedProfileIcons: number[];
    unlockedHats: number[];
    unlockedTitles: number[];
    unlockedBorders: number[];
    equippedProfileIcon: number;
    equippedHat?: number;
    equippedTitle: number;
    equippedBorder: number;
  };
}

let cosmeticCatalogsPromise: Promise<{
  profileIcons: CatalogEntry[];
  hatSkins: CatalogEntry[];
  titles: CatalogEntry[];
  borders: CatalogEntry[];
}> | null = null;
function fetchCosmeticCatalogs() {
  if (!cosmeticCatalogsPromise) {
    cosmeticCatalogsPromise = Promise.all([
      fetch("/data/profile-icons.json").then((r) => r.json()),
      fetch("/data/hats.json").then((r) => r.json()),
      fetch("/data/titles.json").then((r) => r.json()),
      fetch("/data/borders.json").then((r) => r.json()),
    ]).then(([profileIcons, hatSkins, titles, borders]) => ({
      profileIcons,
      hatSkins,
      titles,
      borders,
    }));
  }
  return cosmeticCatalogsPromise;
}

// Static name/sprite catalog for season-reward skins (skin8+) — these are never rows in
// iap_products; ownership comes from player.data.unlockedSkins instead (see season-manager.ts).
let seasonSkinsCatalogPromise: Promise<SeasonSkinCatalogEntry[]> | null = null;
function fetchSeasonSkinsCatalog() {
  if (!seasonSkinsCatalogPromise) {
    seasonSkinsCatalogPromise = fetch("/data/season-skins.json").then((r) => r.json());
  }
  return seasonSkinsCatalogPromise;
}

export class CosmeticsWindow extends BaseWindow {
  private slots: ItemSlot[] = [];
  private iapManager: IAPManager;
  private buyButton: Phaser.GameObjects.Container | null = null;
  private serverProducts: ServerIAPProduct[] = [];
  private combinedProducts: CombinedProduct[] = [];
  private selectedProductIdx: number | null = null;
  private productSprites: Phaser.GameObjects.Sprite[] = [];
  private errorMessageText: Phaser.GameObjects.Text | null = null;
  private rightSideSpinnerContainer: Phaser.GameObjects.Container | null = null;
  private rightSideContentElements: Phaser.GameObjects.GameObject[] = [];
  private notificationHandler: NotificationHandler;
  private syncSpinnerContainer: Phaser.GameObjects.Container | null = null;
  private restoreButton: Phaser.GameObjects.Container | null = null;

  private uiLayer: Phaser.GameObjects.Container;
  private onCloseCallback: (() => void) | null = null;
  private changeButton: Phaser.GameObjects.Container | null = null;
  private closeButton: Phaser.GameObjects.Container | null = null;
  private activeTab: "skins" | CosmeticTab = "skins";
  private selectButton: Phaser.GameObjects.Container | null = null;
  private backButtonRef: ModernButton | null = null;
  private selectedCosmeticEntry: { tab: CosmeticTab; id: number } | null = null;

  constructor(
    scene: Phaser.Scene,
    uiLayer: Phaser.GameObjects.Container,
  ) {
    super(scene);
    this.uiLayer = uiLayer;
    this.iapManager = IAPManager.getInstance();
    this.notificationHandler = new NotificationHandler(scene);
  }

  public setOnClose(callback: () => void): void {
    this.onCloseCallback = callback;
  }

  private async fetchProductsFromServer(
    socket: any,
  ): Promise<ServerIAPProduct[]> {
    console.log("[CosmeticsWindow] fetchProductsFromServer called");
    return new Promise((resolve) => {
      const handler = (response: {
        success: boolean;
        products: ServerIAPProduct[];
        error?: string;
      }) => {
        console.log("[CosmeticsWindow] Received iap-products response:", response);
        socket.off("iap-products", handler);
        if (response.success) {
          resolve(response.products);
        } else {
          console.error(
            "[CosmeticsWindow] Failed to fetch products:",
            response.error,
          );
          resolve([]);
        }
      };

      socket.on("iap-products", handler);

      setTimeout(() => {
        socket.off("iap-products", handler);
        console.error(
          "[CosmeticsWindow] Product fetch timeout - no response from server",
        );
        resolve([]);
      }, 5000);

      socket.emit("get-iap-products", { platform: "apple" });
    });
  }

  private async fetchPurchasedProducts(socket: any): Promise<string[]> {
    return new Promise((resolve) => {
      const handler = (response: {
        success: boolean;
        productIds: string[];
        error?: string;
      }) => {
        socket.off("purchased-products", handler);
        if (response.success) {
          // Return array of purchased product IDs
          resolve(response.productIds);
        } else {
          console.error("Failed to fetch purchased products:", response.error);
          resolve([]);
        }
      };

      socket.on("purchased-products", handler);

      setTimeout(() => {
        socket.off("purchased-products", handler);
        console.error("Purchased products fetch timeout");
        resolve([]);
      }, 5000);

      socket.emit("get-purchased-products");
    });
  }

  private showBuyButton(
    product: CombinedProduct,
    centerX: number,
    btnY: number,
    panelWidth: number,
    height: number,
  ) {
    this.hideBuyButton();

    const button = new ModernButton({
      scene: this.scene,
      x: centerX,
      y: btnY,
      text: i18next.t("ui:buttons.buy") || "BUY",
      style: "success",
      onClick: () => this.handlePurchase(product),
    });

    this.buyButton = this.scene.add.container(0, 0, [button]);
    this.container?.add(this.buyButton);
    this.rightSideContentElements.push(this.buyButton);
  }

  private hideBuyButton() {
    if (this.buyButton) {
      if (this.container && this.container.getAll().includes(this.buyButton)) {
        this.container.remove(this.buyButton, true);
      } else {
        this.buyButton.destroy();
      }
      this.buyButton = null;
    }
  }

  private hideChangeButton() {
    if (this.changeButton) {
      if (this.container && this.container.getAll().includes(this.changeButton)) {
        this.container.remove(this.changeButton, true);
      } else {
        this.changeButton.destroy();
      }
      this.changeButton = null;
    }
  }

  private hideSelectButton(bounds?: { x: number; y: number; width: number; height: number }) {
    if (this.selectButton) {
      if (this.container && this.container.getAll().includes(this.selectButton)) {
        this.container.remove(this.selectButton, true);
      } else {
        this.selectButton.destroy();
      }
      this.selectButton = null;
    }
    // No SELECT button anymore — BACK goes back to sitting centered on its own.
    if (bounds && this.backButtonRef) {
      this.backButtonRef.setPosition(bounds.x + bounds.width / 2, bounds.y + bounds.height - 30);
    }
  }

  private showSelectButton(bounds: { x: number; y: number; width: number; height: number }, onClick: () => void) {
    this.hideSelectButton();
    const btnY = bounds.y + bounds.height - 30;
    const button = new ModernButton({
      scene: this.scene,
      x: 0,
      y: btnY,
      text: i18next.t("ui:buttons.select") || "SELECT",
      style: "success",
      onClick,
    });

    // Lay BACK and SELECT out side by side, centered as a pair, with a small fixed gap
    // between them — SELECT sits to the right of BACK.
    const centerX = bounds.x + bounds.width / 2;
    const backWidth = this.backButtonRef?.buttonWidth ?? 0;
    const selectWidth = button.buttonWidth;
    const totalWidth = backWidth + this.ACTION_BUTTON_GAP + selectWidth;
    const groupLeftX = centerX - totalWidth / 2;
    const backCenterX = groupLeftX + backWidth / 2;
    const selectCenterX = groupLeftX + backWidth + this.ACTION_BUTTON_GAP + selectWidth / 2;

    this.backButtonRef?.setPosition(backCenterX, btnY);
    button.setPosition(selectCenterX, btnY);

    this.selectButton = this.scene.add.container(0, 0, [button]);
    this.selectButton.setScrollFactor(0);
    this.container?.add(this.selectButton);
  }

  private hideCloseButton() {
    if (this.closeButton) {
      if (this.container && this.container.getAll().includes(this.closeButton)) {
        this.container.remove(this.closeButton, true);
      } else {
        this.closeButton.destroy();
      }
      this.closeButton = null;
      this.backButtonRef = null;
    }
  }

  private showChangeButton(product: CombinedProduct, centerX: number, btnY: number) {
    this.hideChangeButton();
    const button = new ModernButton({
      scene: this.scene,
      x: centerX,
      y: btnY,
      text: i18next.t("ui:buttons.change") || "CHANGE",
      style: "success",
      onClick: () => this.handleChangeSkin(product),
    });
    this.changeButton = this.scene.add.container(0, 0, [button]);
    this.container?.add(this.changeButton);
    this.rightSideContentElements.push(this.changeButton);
  }

  private handleChangeSkin(product: CombinedProduct) {
    const socket = (window as any).socket;
    if (!socket) return;
    const skinId = product.id === "player" ? undefined : product.id;
    socket.emit("change-player-skin", { skinId });
    const responseHandler = (response: { success: boolean; skinId?: string; error?: string }) => {
      socket.off("skin-change-result", responseHandler);
      if (response.success) {
        trackEvent("iap-skin-changed", { skin: product.name });
        if (window.player && window.player.data) {
          window.player.data.activeSkin = response.skinId;
          if (window.player.updateSkin) {
            window.player.updateSkin(response.skinId);
          }
        }
        this.hide();
      } else {
        console.error("[CosmeticsWindow] Failed to change skin:", response.error);
      }
    };
    socket.on("skin-change-result", responseHandler);
    setTimeout(() => {
      socket.off("skin-change-result", responseHandler);
      console.warn("[CosmeticsWindow] Skin change timeout");
    }, 5000);
  }

  protected getLayoutOverrides(): Partial<{ x: number; y: number; width: number; height: number }> {
    const gameWidth = this.scene.scale.width;
    const gameHeight = this.scene.scale.height;
    const marginLeft = 120;
    const marginRight = 32;
    const width = gameWidth - marginLeft - marginRight;
    // Center the window horizontally instead of anchoring it to the left margin.
    const x = (gameWidth - width) / 2;
    const y = 32;
    return {
      x,
      y,
      width,
      height: gameHeight - y - 20,
    };
  }

  // Vertical gap between the window header and the tab bar below it
  private readonly TAB_BAR_GAP = 8;
  // Height of the tab bar itself (used to compute where content starts)
  private readonly TAB_HEIGHT = 32;
  // Vertical gap between the tab bar and the content below it
  private readonly TAB_CONTENT_GAP = 12;
  // Horizontal gap between the BACK and SELECT buttons when both are shown side by side.
  private readonly ACTION_BUTTON_GAP = 8;

  // Y position where content should start, below the header and tab bar
  private getContentStartY(y: number): number {
    return y + this.HEADER_HEIGHT + this.TAB_BAR_GAP + this.TAB_HEIGHT + this.TAB_CONTENT_GAP;
  }

  private buildTabBarElements(x: number, y: number, width: number): Phaser.GameObjects.GameObject[] {
    const tabs: { key: "skins" | CosmeticTab; label: string }[] = [
      { key: "skins", label: "SKINS" },
      { key: "profileIcons", label: "ICONS" },
      { key: "hats", label: "HATS" },
      { key: "titles", label: "TITLES" },
      { key: "borders", label: "BORDERS" },
    ];
    const tabBarY = y + this.HEADER_HEIGHT + this.TAB_BAR_GAP;
    const tabHeight = this.TAB_HEIGHT;
    const tabSpacing = 6;
    const tabWidth = (width - this.CONTENT_PADDING_X * 2 - tabSpacing * (tabs.length - 1)) / tabs.length;
    const tabBarX = x + this.CONTENT_PADDING_X;

    const elements: Phaser.GameObjects.GameObject[] = [];
    tabs.forEach((tab, i) => {
      const isSelected = this.activeTab === tab.key;
      const cx = tabBarX + i * (tabWidth + tabSpacing) + tabWidth / 2;
      const rect = this.scene.add
        .rectangle(cx, tabBarY + tabHeight / 2, tabWidth, tabHeight, isSelected ? 0xffe066 : 0x1a3550, isSelected ? 0.95 : 0.7)
        .setScrollFactor(0)
        .setInteractive({ useHandCursor: true });
      const label = this.scene.add
        .text(cx, tabBarY + tabHeight / 2, tab.label, {
          fontSize: "13px",
          color: isSelected ? "#222222" : COLORS.WHITE,
          fontFamily: "Anton",
        })
        .setOrigin(0.5)
        .setScrollFactor(0);
      rect.on("pointerdown", () => {
        if (this.activeTab === tab.key) return;
        this.activeTab = tab.key;
        this.show();
      });
      elements.push(rect, label);
      this.productSprites.push(rect as any, label as any);
    });
    return elements;
  }

  async show(options: { skipBackgroundSync?: boolean } = {}) {
    console.log("[CosmeticsWindow] show() called");
    const wasAlreadyOpen = this.visible;
    // Rebuild visuals without firing onCloseCallback — this is a redraw (e.g. switching
    // tabs), not the user actually closing the window.
    this.hide(false);
    if (!wasAlreadyOpen) {
      trackEvent("iap-window-opened");
    }
    this.selectedProductIdx = null;
    this.selectedCosmeticEntry = null;
    this.rightSideContentElements = [];

    // Create base window elements (background, title) using center side for full width
    const baseElements = this.createBaseWindow({
      title: this.activeTab === "skins" ? i18next.t("ui:playerSkinsStore") : "COSMETICS",
      side: "center",
    });

    // Get window bounds
    const bounds = this.getWindowBounds()!;
    const { x, y, width, height } = bounds;
    console.log("[CosmeticsWindow] Window bounds:", { x, y, width, height });

    const tabBarElements = this.buildTabBarElements(x, y, width);

    if (this.activeTab !== "skins") {
      this.container = this.scene.add.container(0, 0, [...baseElements, ...tabBarElements]);
      const backButton = new ModernButton({
        scene: this.scene,
        x: x + width / 2,
        y: y + height - 30,
        text: i18next.t("ui:buttons.back") || "BACK",
        style: "secondary",
        onClick: () => this.hide(),
      });
      this.backButtonRef = backButton;
      this.closeButton = this.scene.add.container(0, 0, [backButton]);
      this.closeButton.setScrollFactor(0);
      this.container.add(this.closeButton);
      this.container.setDepth(100000);
      this.container.setScrollFactor(0);
      this.container.setVisible(true);
      this.visible = true;
      await this.showCosmeticTab(this.activeTab, bounds);
      return;
    }

    // Left half for grid, right half for info
    const contentStartY = this.getContentStartY(y);
    const infoX = x + width * 0.75 + this.CONTENT_PADDING_X;
    const infoY = contentStartY;
    const infoWidth = width / 2 - this.CONTENT_PADDING_X * 2;
    const bottomY = y + height - this.CONTENT_PADDING_Y;

    // Product info text (upper right)
    this.errorMessageText = this.scene.add
      .text(x + width / 2 + this.CONTENT_PADDING_X, contentStartY + 8, "", {
        fontSize: "16px",
        color: COLORS.ORANGE,
        fontFamily: "Anton",
        align: "right",
      })
      .setOrigin(1, 0);
    this.errorMessageText.setScrollFactor(0);
    this.rightSideContentElements.push(this.errorMessageText);

    // Create skin name text at the top (initially hidden)
    const productNameText = this.scene.add
      .text(infoX, contentStartY, "", {
        fontSize: "24px",
        color: COLORS.YELLOW,
        fontFamily: "Anton",
        align: "center",
        wordWrap: { width: infoWidth },
      })
      .setShadow(2, 2, "#000000", 0.7)
      .setOrigin(0.5, 0);
    productNameText.setScrollFactor(0);
    this.rightSideContentElements.push(productNameText);

    // Create large preview sprite below name (initially hidden)
    const previewSize = 128;
    const previewY = contentStartY + 80;
    const previewSprite = this.scene.add.sprite(infoX, previewY, "player", 0);
    previewSprite.setDisplaySize(previewSize, previewSize);
    previewSprite.setScrollFactor(0);
    previewSprite.setVisible(false);
    this.productSprites.push(previewSprite);
    this.rightSideContentElements.push(previewSprite);

    // Create description text below sprite (initially hidden)
    const descY = previewY + previewSize / 2 + 8;
    const productDescText = this.scene.add
      .text(infoX, descY, "", {
        fontSize: "16px",
        color: COLORS.WHITE,
        fontFamily: "Anton",
        align: "center",
        wordWrap: { width: infoWidth },
      })
      .setOrigin(0.5, 0);
    productDescText.setScrollFactor(0);
    this.rightSideContentElements.push(productDescText);

    // Create price box and text (initially hidden) - will be at bottom with buy button
    const productPriceBox = this.scene.add.graphics();
    productPriceBox.setScrollFactor(0);
    productPriceBox.setVisible(false);
    // Do NOT set interactive or pointerdown on price box
    this.productSprites.push(productPriceBox as any);
    this.rightSideContentElements.push(productPriceBox);

    const productPriceText = this.scene.add
      .text(infoX, bottomY, "", {
        fontSize: "20px",
        color: COLORS.YELLOW,
        fontFamily: "Anton",
        align: "center",
      })
      .setOrigin(0.5, 0.5);
    productPriceText.setScrollFactor(0);
    productPriceText.setVisible(false);
    productPriceText.setInteractive({ useHandCursor: false });
    productPriceText.on("pointerdown", (e: any) => e.stopPropagation());
    this.rightSideContentElements.push(productPriceText);

    // Create OWNED box (initially hidden)
    const ownedBox = this.scene.add.graphics();
    ownedBox.setScrollFactor(0);
    ownedBox.setVisible(false);
    this.productSprites.push(ownedBox as any);
    this.rightSideContentElements.push(ownedBox);

    const ownedText = this.scene.add
      .text(infoX, bottomY, "OWNED", {
        fontSize: "16px",
        color: COLORS.BLACK,
        fontFamily: "Anton",
        align: "center",
      })
      .setOrigin(0.5, 0.5);
    ownedText.setScrollFactor(0);
    ownedText.setVisible(false);
    this.productSprites.push(ownedText as any);
    this.rightSideContentElements.push(ownedText);

    // Create loading spinner for window (small version)
    const spinnerSize = 64;
    const spinner = this.scene.add.image(
      x + width / 2,
      y + height / 2,
      "logo-portal",
    );
    spinner.setDisplaySize(spinnerSize, spinnerSize);
    spinner.setScrollFactor(0);

    // Add rotation tween for spinning effect
    const spinnerTween = this.scene.tweens.add({
      targets: spinner,
      angle: 360,
      duration: 2000,
      repeat: -1,
      ease: "Linear",
    });

    const loadingText = this.scene.add
      .text(
        x + width / 2,
        y + height / 2 + spinnerSize / 2 + 16,
        i18next.t("ui:instructions.loading") || "Loading...",
        {
          fontSize: "16px",
          color: COLORS.WHITE,
          fontFamily: "Anton",
          align: "center",
        },
      )
      .setOrigin(0.5, 0);
    loadingText.setScrollFactor(0);

    // Create initial container with base elements first (background renders behind everything)
    // Start with just base elements and info panel elements
    this.container = this.scene.add.container(0, 0, [
      ...baseElements,
      ...tabBarElements,
      this.errorMessageText,
      productNameText,
      productDescText,
      productPriceBox,
      productPriceText,
      ownedBox,
      ownedText,
      spinner,
      loadingText,
    ]);

    // Add back button to container after creation to avoid affecting layout
    const closeButton = new ModernButton({
      scene: this.scene,
      x: x + width / 2,
      y: y + height - 30,
      text: i18next.t("ui:buttons.back") || "BACK",
      style: "secondary",
      onClick: () => this.hide(),
    });
    this.closeButton = this.scene.add.container(0, 0, [closeButton]);
    this.closeButton.setScrollFactor(0);
    this.container.add(this.closeButton);

    // Manual "Restore Purchases" button, bottom-left - the automatic background
    // sync (performBackgroundSync) covers the common case, this is a fallback the
    // user can trigger themselves.
    if (isTouchDevice() && this.iapManager.isAvailable()) {
      const restoreButton = new ModernButton({
        scene: this.scene,
        x: x + this.CONTENT_PADDING_X + 70,
        y: y + height - 30,
        text: i18next.t("ui:buttons.restorePurchases") || "RESTORE PURCHASES",
        style: "secondary",
        size: "small",
        onClick: () => this.handleRestoreButton(),
      });
      this.restoreButton = this.scene.add.container(0, 0, [restoreButton]);
      this.restoreButton.setScrollFactor(0);
      this.container.add(this.restoreButton);
    }

    this.container.setDepth(100000);
    this.container.setScrollFactor(0);
    this.container.setVisible(true);
    this.visible = true;

    // Now fetch products from server
    const socket = (window as any).socket;
    if (!socket) {
      console.error("[CosmeticsWindow] Socket not available");

      // Remove spinner
      spinnerTween.stop();
      spinner.destroy();
      loadingText.destroy();

      const errorText = this.scene.add
        .text(x, y, i18next.t("ui:instructions.connectionError") || "Connection error", {
          fontSize: "18px",
          color: COLORS.ORANGE,
          fontFamily: "Anton",
          align: "center",
        })
        .setOrigin(0.5, 0.5);
      errorText.setScrollFactor(0);
      this.container.add(errorText);
      return;
    }

    console.log("[CosmeticsWindow] Socket available, fetching products");
    // This tab is the Player Skins Store — exclude non-skin products (e.g. the season
    // pass, which is sold separately from the season screen's ACTIVATE button).
    this.serverProducts = (await this.fetchProductsFromServer(socket)).filter(
      (p) => p.product_type === "skin",
    );
    console.log("[CosmeticsWindow] Received products:", this.serverProducts.length, "items");

    // Season-reward skins (skin8+) aren't IAP products — they're granted straight onto
    // player.data.unlockedSkins by SeasonManager, so pull the name/sprite catalog and
    // cross-reference it against what this player has actually unlocked.
    const seasonSkinsCatalog = await fetchSeasonSkinsCatalog();
    const unlockedSeasonSkinIds: string[] = (window as any).player?.data?.unlockedSkins || [];
    const unlockedSeasonSkins = seasonSkinsCatalog.filter((s) => unlockedSeasonSkinIds.includes(s.id));

    if (this.serverProducts.length === 0 && unlockedSeasonSkins.length === 0) {
      console.warn("[CosmeticsWindow] No products returned from server");

      // Remove spinner
      spinnerTween.stop();
      spinner.destroy();
      loadingText.destroy();

      const noProductsText = this.scene.add
        .text(x + width / 2, y + height / 2, i18next.t("ui:instructions.noProductsAvailable") || "No products available", {
          fontSize: "18px",
          color: COLORS.GRAY,
          fontFamily: "Anton",
          align: "center",
        })
        .setOrigin(0.5, 0.5);
      noProductsText.setScrollFactor(0);
      this.container.add(noProductsText);
      return;
    }

    // Detect login state
    const isLoggedIn =
      typeof window !== "undefined" &&
      window.player &&
      window.player.data &&
      window.player.data.mailVerified;
    // Fetch purchased products only if logged in
    let purchasedProductIds: string[] = [];
    if (isLoggedIn) {
      purchasedProductIds = await this.fetchPurchasedProducts(socket);
    }

    // Fetch platform-specific prices
    let platformProducts: IAPProduct[] = [];
    let platformKey: "apple_product_id" | "google_product_id" =
      "apple_product_id";
    let platformName = "iOS";
    if (isTouchDevice() && this.iapManager.isAvailable()) {
      if (this.iapManager.getPlatform() === "ios") {
        platformKey = "apple_product_id";
        platformName = "iOS";
      } else if (this.iapManager.getPlatform() === "android") {
        platformKey = "google_product_id";
        platformName = "Android";
      }
      const productIds = this.serverProducts
        .map((p: ServerIAPProduct) => p[platformKey])
        .filter((id: string | null) => id !== null) as string[];

      platformProducts = await this.iapManager.fetchProducts(productIds);
    }

    // Combine server products with platform prices
    this.combinedProducts = this.serverProducts.map((serverProduct) => {
      let platformProduct: IAPProduct | undefined;
      if (isTouchDevice() && this.iapManager.isAvailable()) {
        if (platformKey === "apple_product_id") {
          platformProduct = platformProducts.find(
            (p) => p.productId === serverProduct.apple_product_id,
          );
        } else if (platformKey === "google_product_id") {
          platformProduct = platformProducts.find(
            (p) => p.productId === serverProduct.google_product_id,
          );
        }
      }
      return {
        ...serverProduct,
        appleProductId: serverProduct.apple_product_id || undefined,
        googleProductId: serverProduct.google_product_id || undefined,
        priceFormatted: platformProduct?.priceFormatted || (isTouchDevice() ? i18next.t("ui:instructions.loading") || "Loading..." : "Skin"),
      };
    });

    // Add default skin at beginning of products list
    this.combinedProducts.unshift({
      id: "player",
      product_type: "skin",
      name: "Default",
      description: "",
      apple_product_id: null,
      google_product_id: null,
      amount: 0,
      display_order: -1,
      enabled: true,
    });

    // Append unlocked season-reward skins at the end — always owned, never purchasable here.
    unlockedSeasonSkins.forEach((seasonSkin, idx) => {
      this.combinedProducts.push({
        id: seasonSkin.id,
        product_type: "skin",
        name: seasonSkin.name || seasonSkin.id,
        description: i18next.t("ui:instructions.seasonSkinDescription") || "Season reward skin",
        apple_product_id: null,
        google_product_id: null,
        amount: 0,
        display_order: 1000 + idx,
        enabled: true,
      });
    });

    const activeSkinId: string = (window as any).player?.data?.activeSkin || "player";

    // Remove loading spinner from container before adding grid
    this.container.remove([spinner, loadingText]);
    spinnerTween.stop();
    spinner.destroy();
    loadingText.destroy();

    // Create grid layout on left side
    this.slots = [];
    const gridCols = 5; // 5 columns for more compact layout
    const gridRows = Math.ceil(this.combinedProducts.length / gridCols);

    const contentWidth = width / 2 - this.CONTENT_PADDING_X * 2;

    const tempSlot = new ItemSlot(0, 0);
    const slotSize = tempSlot.size;
    const slotSpacingX = (contentWidth - slotSize * gridCols) / (gridCols + 1); // Tighter horizontal spacing
    const slotSpacingY = 48; // Double vertical spacing for price text

    // Grid starts at left side of window
    const gridStartX = x + this.CONTENT_PADDING_X;
    const gridStartY = contentStartY;

    for (let row = 0; row < gridRows; row++) {
      for (let col = 0; col < gridCols; col++) {
        const slotIdx = row * gridCols + col;
        if (slotIdx >= this.combinedProducts.length) break;

        const product = this.combinedProducts[slotIdx];
        const slotX = gridStartX + col * (slotSize + slotSpacingX);
        const slotY = gridStartY + row * (slotSize + slotSpacingY);

        const isPurchased =
          product.id === "player" ||
          purchasedProductIds.includes(product.id) ||
          unlockedSeasonSkinIds.includes(product.id);
        const isActive = product.id === activeSkinId;

        const selected = this.selectedProductIdx === slotIdx;
        const itemSlot = new ItemSlot(slotX, slotY, true);
        itemSlot.setSelected(selected);
        if (isActive) {
          itemSlot.setMaterialOutlineColor(hexToPhaser(COLORS.GREEN));
        }
        itemSlot.createRectangle(this.scene);

        // Create sprite using product id (skin1, skin2, skin3, etc.)
        const sprite = this.scene.add.sprite(
          slotX + slotSize / 2,
          slotY + slotSize / 2,
          product.id, // Use the product ID directly as the texture key
          0,
        );
        sprite.displayWidth = slotSize * 0.85;
        sprite.displayHeight = slotSize * 0.85;
        sprite.setScrollFactor(0);

        // Create 2-frame animation for this product if not already created
        const animKey = `${product.id}-idle`;
        if (!this.scene.anims.exists(animKey)) {
          const frames = this.scene.anims.generateFrameNumbers(product.id, {
            start: 0,
            end: 1,
          });
          if (frames && frames.length > 0) {
            this.scene.anims.create({
              key: animKey,
              frames: frames,
              frameRate: 4,
              repeat: -1,
            });
          }
        }
        if (this.scene.anims.exists(animKey)) {
          sprite.play(animKey);
        }

        this.productSprites.push(sprite);

        // Add price box with yellow background and black text
        const priceText = isActive
          ? "ACTIVE"
          : isPurchased
            ? "OWNED"
            : !isLoggedIn ? "Skin" : product.priceFormatted || "Loading...";
        const tempText = this.scene.add.text(0, 0, priceText, {
          fontSize: "14px",
          fontFamily: "Anton",
        });
        const textWidth = tempText.width;
        const textHeight = tempText.height;
        tempText.destroy();

        // Create yellow background box (or green if owned)
        const boxPadding = 4;
        const boxWidth = textWidth + boxPadding * 2;
        const boxHeight = textHeight + boxPadding * 2;
        const boxX = slotX + slotSize / 2 - boxWidth / 2;
        const boxY = slotY + slotSize + 4;

        const priceBox = this.scene.add.graphics();
        priceBox.fillStyle(
          hexToPhaser((isActive || isPurchased) ? COLORS.GREEN : COLORS.YELLOW),
          1,
        );
        priceBox.fillRect(boxX, boxY, boxWidth, boxHeight);
        priceBox.lineStyle(2, hexToPhaser(COLORS.BLACK), 1);
        priceBox.strokeRect(boxX, boxY, boxWidth, boxHeight);
        priceBox.setScrollFactor(0);
        this.productSprites.push(priceBox as any);

        // Add price text in black
        const priceTextObj = this.scene.add
          .text(
            slotX + slotSize / 2,
            slotY + slotSize + 4 + boxPadding,
            priceText,
            {
              fontSize: "14px",
              color: COLORS.BLACK,
              fontFamily: "Anton",
              align: "center",
            },
          )
          .setOrigin(0.5, 0);
        priceTextObj.setScrollFactor(0);
        this.productSprites.push(priceTextObj as any);

        // Click handler - works for both purchased and non-purchased
        if (itemSlot.rect) {
          itemSlot.rect.on("pointerdown", () => {
            playSound(this.scene, "menu-click-sound", null, 0.5);

            if (this.selectedProductIdx === slotIdx) {
              // Deselect
              this.selectedProductIdx = null;
              itemSlot.setSelected(false);
              itemSlot.applyBorderStyle();
              this.hideBuyButton();
              this.hideChangeButton();
              productNameText.setText("");
              productDescText.setText("");
              productPriceText.setText("");
              productPriceText.setVisible(false);
              productPriceBox.clear();
              productPriceBox.setVisible(false);
              ownedBox.clear();
              ownedBox.setVisible(false);
              ownedText.setVisible(false);
              previewSprite.setVisible(false);
              previewSprite.stop();
            } else {
              // Deselect previous
              if (this.selectedProductIdx !== null) {
                this.slots[this.selectedProductIdx]?.setSelected(false);
                this.slots[this.selectedProductIdx]?.applyBorderStyle();
              }
              this.selectedProductIdx = slotIdx;
              itemSlot.setSelected(true);
              itemSlot.applyBorderStyle();

              // Update preview sprite with animation
              previewSprite.setTexture(product.id, 0);
              const previewAnimKey = `${product.id}-preview`;
              if (!this.scene.anims.exists(previewAnimKey)) {
                const previewFrames = this.scene.anims.generateFrameNumbers(
                  product.id,
                  {
                    start: 0,
                    end: 1,
                  },
                );
                if (previewFrames && previewFrames.length > 0) {
                  this.scene.anims.create({
                    key: previewAnimKey,
                    frames: previewFrames,
                    frameRate: 4,
                    repeat: -1,
                  });
                }
              }
              if (this.scene.anims.exists(previewAnimKey)) {
                previewSprite.play(previewAnimKey);
              }
              previewSprite.setVisible(true);

              // Update name and description
              productNameText.setText(product.name);
              productDescText.setText(isActive ? "" : (i18next.t("ui:instructions.skinDescription") || product.description || ""));

              if (isActive) {
                // Show ACTIVE label — this skin is already equipped
                productPriceText.setVisible(false);
                productPriceBox.clear();
                productPriceBox.setVisible(false);
                this.hideBuyButton();
                this.hideChangeButton();

                const ownedPadding = 8;
                ownedText.setText("ACTIVE");
                const ownedWidth = ownedText.width + ownedPadding * 2;
                const ownedHeight = ownedText.height + ownedPadding * 2;

                ownedBox.clear();
                ownedBox.fillStyle(hexToPhaser(COLORS.GREEN), 1);
                ownedBox.fillRect(
                  infoX - ownedWidth / 2,
                  bottomY - ownedHeight / 2,
                  ownedWidth,
                  ownedHeight,
                );
                ownedBox.lineStyle(3, hexToPhaser(COLORS.BLACK), 1);
                ownedBox.strokeRect(
                  infoX - ownedWidth / 2,
                  bottomY - ownedHeight / 2,
                  ownedWidth,
                  ownedHeight,
                );
                ownedBox.setVisible(true);
                ownedText.setVisible(true);
              } else if (isPurchased) {
                // Show CHANGE button — owned but not currently active
                productPriceText.setVisible(false);
                productPriceBox.clear();
                productPriceBox.setVisible(false);
                ownedBox.clear();
                ownedBox.setVisible(false);
                ownedText.setVisible(false);
                this.hideBuyButton();
                this.showChangeButton(product, infoX, bottomY);
              } else if (!isTouchDevice()) {
                // Not mobile and skin not owned: purchasing not available here
                productDescText.setText("");
                productPriceText.setVisible(true);
                productPriceText.setText(
                  i18next.t("ui:instructions.skinsOnlyMobile") || "Skins can only be bought on IOS and Android, but can still be used on all platforms",
                );
                productPriceText.setWordWrapWidth(200);
                productPriceText.setFontSize(12);
                productPriceBox.clear();
                productPriceBox.setVisible(false);
                this.hideBuyButton();
                this.hideChangeButton();
                ownedBox.clear();
                ownedBox.setVisible(false);
                ownedText.setVisible(false);
              } else if (!isLoggedIn) {
                // Hide price and buy button, show login prompt
                this.hideBuyButton();
                this.hideChangeButton();
                productPriceText.setVisible(true);
                productPriceText.setText(
                  i18next.t("ui:instructions.loginForSkins") || "Login or verify your email to access skins.",
                );
                productPriceText.setWordWrapWidth(260);
                productPriceText.setFontSize(14);
                productPriceBox.clear();
                productPriceBox.setVisible(false);
                ownedBox.clear();
                ownedBox.setVisible(false);
                ownedText.setVisible(false);
              } else {
                // Hide OWNED box, show price and buy button
                ownedBox.clear();
                ownedBox.setVisible(false);
                ownedText.setVisible(false);

                // Update price with yellow box on left side
                const priceText = product.priceFormatted || "Loading...";
                productPriceText.setText(priceText);
                productPriceText.setColor(COLORS.BLACK);
                productPriceText.setVisible(true);

                // Calculate and draw price box
                const boxPadding = 8;
                const boxWidth = productPriceText.width + boxPadding * 2;
                const boxHeight = productPriceText.height + boxPadding * 2;

                // Position buy button and price badge side by side
                let buyBtnX = infoX;
                let priceBoxX = infoX + 60; // 110px to the right of button
                let priceBoxY = bottomY - boxHeight / 2;

                // Only show buy button if user is logged in
                if (isTouchDevice() && isLoggedIn) {
                  this.showBuyButton(
                    product,
                    buyBtnX,
                    bottomY,
                    width / 2,
                    height,
                  );
                }

                // Draw price box to the right of the buy button
                productPriceBox.clear();
                productPriceBox.fillStyle(hexToPhaser(COLORS.YELLOW), 1);
                productPriceBox.fillRect(
                  priceBoxX,
                  priceBoxY,
                  boxWidth,
                  boxHeight,
                );
                productPriceBox.lineStyle(3, hexToPhaser(COLORS.BLACK), 1);
                productPriceBox.strokeRect(
                  priceBoxX,
                  priceBoxY,
                  boxWidth,
                  boxHeight,
                );
                productPriceBox.setVisible(true);

                // Move price text to center of price box
                productPriceText.setPosition(
                  priceBoxX + boxWidth / 2,
                  priceBoxY + boxHeight / 2,
                );
              }
            }
          });
        }

        this.slots.push(itemSlot);
      }
    }

    // Add grid elements to existing container in the correct order:
    // 1. Background stays first (already in container)
    // 2. Add slot rectangles next (behind sprites)
    // 3. Add product sprites (on top of rectangles)
    // This ensures grid elements render on top of the background
    const slotRectangles = this.slots
      .map((s) => s.rect)
      .filter((r) => r !== null);

    this.container.add(slotRectangles);
    this.container.add(this.productSprites);

    // Fire-and-forget: check Apple/Google for anything our server missed, without
    // blocking the shop from rendering immediately with what we already have.
    if (!options.skipBackgroundSync) {
      this.performBackgroundSync();
    }
  }

  private async handlePurchase(product: CombinedProduct) {
    const platform = this.iapManager.getPlatform();
    const nativeProductId = platform === "android" ? product.googleProductId : product.appleProductId;

    if (!nativeProductId) {
      console.error("[CosmeticsWindow] No native product ID for platform:", platform);
      return;
    }

    // Show right-side spinner immediately while waiting for native purchase interface
    this.showRightSideSpinner();

    const result = await this.iapManager.purchaseProduct(nativeProductId);

    if (result.success && result.receipt) {
      console.info(
        "[CosmeticsWindow] Purchase successful, sending receipt to server for verification...",
      );

      const socket = (window as any).socket;
      if (socket) {
        // Hide spinner
        this.hideRightSideSpinner();

        const platform = this.iapManager.getPlatform();
        const purchaseData: any = {
          productId: product.id, // Use database product ID (skin1, skin2, skin3), not Apple ID
          platform:
            platform === "ios"
              ? "apple"
              : platform === "android"
                ? "google"
                : "apple",
        };

        if (platform === "ios") {
          purchaseData.appleProductId = result.productId;
          purchaseData.receiptData = result.receipt;
        } else if (platform === "android") {
          // Android receipt contains purchase token and signature
          const androidReceipt = result.receipt as any;
          purchaseData.purchaseToken = androidReceipt.purchaseToken;
          purchaseData.signature = androidReceipt.signature;
          purchaseData.receiptData = androidReceipt.originalJson || "";
        }

        trackEvent("iap-purchase-attempt");

        socket.emit("iap-purchase", purchaseData);
        console.info(
          "[CosmeticsWindow] Receipt sent to server, waiting for verification...",
        );

        const verificationHandler = (response: {
          success: boolean;
          error?: string;
        }) => {
          socket.off("iap-purchase-verified", verificationHandler);

          if (response.success) {
            trackEvent("iap-purchase-success", { productId: product.id });
            console.info("[CosmeticsWindow] Purchase verified, equipping skin...");
            this.notificationHandler.show(
              `✓ ${product.name} purchased!`,
              "success",
              3000,
            );
            showConfetti(this.scene);

            // Only now safe to finish/acknowledge with the store - the server has
            // confirmed it recorded the purchase.
            if (platform === "ios") {
              this.iapManager.confirmTransaction(result.productId!);
            } else if (platform === "android") {
              this.iapManager.acknowledgePurchase(purchaseData.purchaseToken);
            }

            // Equip the newly purchased skin immediately
            socket.emit("change-player-skin", { skinId: product.id });
            const skinChangeHandler = (skinResponse: { success: boolean; skinId?: string; error?: string }) => {
              socket.off("skin-change-result", skinChangeHandler);
              if (skinResponse.success) {
                if (window.player && window.player.data) {
                  window.player.data.activeSkin = skinResponse.skinId;
                  if (window.player.updateSkin) {
                    window.player.updateSkin(skinResponse.skinId);
                  }
                }
              }
              this.hide();
            };
            socket.on("skin-change-result", skinChangeHandler);
            setTimeout(() => {
              socket.off("skin-change-result", skinChangeHandler);
              this.hide();
            }, 5000);
          } else {
            trackEvent("iap-purchase-failure", { error: response.error });
            console.error("[CosmeticsWindow] Verification failed:", response.error);
            // Show error message popup
            this.notificationHandler.show(
              `✗ ${response.error || "Purchase failed"}`,
              "error",
              5000,
            );
          }
        };

        socket.on("iap-purchase-verified", verificationHandler);

        setTimeout(() => {
          socket.off("iap-purchase-verified", verificationHandler);
          console.warn("[CosmeticsWindow] Verification timeout after 15 seconds");
          this.hideRightSideSpinner();
          this.notificationHandler.show(
            "✗ Purchase verification timeout",
            "error",
            5000,
          );
        }, 15000);
      } else {
        // The purchase itself is still safe - it isn't finished/acknowledged with the
        // store yet, so it'll be retried (native redelivery, or the next background
        // sync) once the connection is back. Just let the user know now.
        this.hideRightSideSpinner();
        this.notificationHandler.show(
          "✗ Not connected to server - please try again",
          "error",
          5000,
        );
      }
    } else {
      console.error(
        "[CosmeticsWindow] Purchase failed or cancelled:",
        result.error,
      );
      // Hide spinner if purchase failed or was cancelled
      this.hideRightSideSpinner();

      if (result.error) {
        // Show error message (don't close window if user cancelled)
        if (this.errorMessageText) {
          this.errorMessageText.setText(`✗ ${result.error}`);
          setTimeout(() => {
            if (this.errorMessageText) {
              this.errorMessageText.setText("");
            }
          }, 5000);
        }
      }
    }
    console.log("[CosmeticsWindow] show() method completed");
  }

  private fetchSeasonData(socket: any): Promise<SeasonDataForCosmetics | null> {
    if (!socket) return Promise.resolve(null);
    return new Promise((resolve) => {
      let done = false;
      const handler = (data: any) => {
        if (done) return;
        done = true;
        socket.off("season-data", handler);
        resolve(data ?? null);
      };
      socket.once("season-data", handler);
      socket.emit("get-season-data");
      setTimeout(() => {
        if (done) return;
        done = true;
        socket.off("season-data", handler);
        resolve(null);
      }, 5000);
    });
  }

  private async showCosmeticTab(
    tab: CosmeticTab,
    bounds: { x: number; y: number; width: number; height: number },
  ): Promise<void> {
    const { x, y, width, height } = bounds;
    const socket = (window as any).socket;

    // Small in-window spinner (not a full-screen overlay) so re-fetching on every tab
    // switch doesn't flash the whole window to black.
    const spinnerSize = 48;
    const spinner = this.scene.add.image(x + width / 2, y + height / 2, "logo-portal");
    spinner.setDisplaySize(spinnerSize, spinnerSize);
    spinner.setScrollFactor(0);
    this.container!.add(spinner);
    this.productSprites.push(spinner as any);
    const spinnerTween = this.scene.tweens.add({
      targets: spinner,
      angle: 360,
      duration: 1000,
      repeat: -1,
      ease: "Linear",
    });

    const [seasonData, catalogs] = await Promise.all([this.fetchSeasonData(socket), fetchCosmeticCatalogs()]);

    spinnerTween.stop();
    if (spinner.scene) spinner.destroy();

    if (!this.container) return;

    if (!seasonData) {
      const errorText = this.scene.add
        .text(x + width / 2, y + height / 2, "Unable to load season data", {
          fontSize: "16px",
          color: COLORS.GRAY,
          fontFamily: "Anton",
        })
        .setOrigin(0.5)
        .setScrollFactor(0);
      this.productSprites.push(errorText as any);
      this.container.add(errorText);
      return;
    }

    const catalogFor: CatalogEntry[] =
      tab === "profileIcons"
        ? catalogs.profileIcons
        : tab === "titles"
          ? catalogs.titles
          : tab === "borders"
            ? catalogs.borders
            : catalogs.hatSkins;
    const unlockedIds: number[] =
      tab === "profileIcons"
        ? seasonData.cosmetics.unlockedProfileIcons
        : tab === "titles"
          ? seasonData.cosmetics.unlockedTitles
          : tab === "borders"
            ? seasonData.cosmetics.unlockedBorders
            : seasonData.cosmetics.unlockedHats;
    const equippedId: number | undefined =
      tab === "profileIcons"
        ? seasonData.cosmetics.equippedProfileIcon
        : tab === "titles"
          ? seasonData.cosmetics.equippedTitle
          : tab === "borders"
            ? seasonData.cosmetics.equippedBorder
            : seasonData.cosmetics.equippedHat;
    const equipSlot: "profileIcon" | "hatSkin" | "title" | "border" =
      tab === "profileIcons"
        ? "profileIcon"
        : tab === "titles"
          ? "title"
          : tab === "borders"
            ? "border"
            : "hatSkin";

    // Only show items the player has actually unlocked — no locked/preview entries.
    const unlockedCatalog = catalogFor.filter((entry) => unlockedIds.includes(entry.id));
    // Hats, titles, and borders can be unequipped, unlike the other cosmetic slots,
    // so always offer a "None" tile for them.
    if (tab === "hats") {
      unlockedCatalog.unshift({ id: NONE_HAT_ID, name: "None" });
    } else if (tab === "titles") {
      unlockedCatalog.unshift({ id: NONE_TITLE_ID, text: "None" });
    } else if (tab === "borders") {
      unlockedCatalog.unshift({ id: NONE_BORDER_ID, text: "None" });
    }

    const gridCols = 5;
    const slotSize = 72;
    const slotSpacingX = 16;
    const slotSpacingY = 44;
    const gridStartX = x + this.CONTENT_PADDING_X;
    const gridStartY = this.getContentStartY(y);

    if (unlockedCatalog.length === 0) {
      const emptyKey =
        tab === "profileIcons"
          ? "noIconsUnlocked"
          : tab === "titles"
            ? "noTitlesUnlocked"
            : tab === "borders"
              ? "noBordersUnlocked"
              : "noHatsUnlocked";
      const emptyText = this.scene.add
        .text(x + width / 2, y + height / 2, i18next.t(`ui:instructions.${emptyKey}`), {
          fontSize: "16px",
          color: COLORS.GRAY,
          fontFamily: "Anton",
        })
        .setOrigin(0.5)
        .setScrollFactor(0);
      this.productSprites.push(emptyText as any);
      this.container!.add(emptyText);
      return;
    }

    // Objects created by renderGrid — tracked separately from productSprites so a
    // reselect (no server round-trip) can tear down and rebuild just the grid tiles.
    let gridObjs: Phaser.GameObjects.GameObject[] = [];

    const renderGrid = () => {
      gridObjs.forEach((obj) => {
        if (obj && (obj as any).scene) obj.destroy();
      });
      gridObjs = [];

      unlockedCatalog.forEach((entry, i) => {
        const col = i % gridCols;
        const row = Math.floor(i / gridCols);
        const slotX = gridStartX + col * (slotSize + slotSpacingX) + slotSize / 2;
        const slotY = gridStartY + row * (slotSize + slotSpacingY) + slotSize / 2;

        const isEquipped =
          tab === "hats" && entry.id === NONE_HAT_ID ? !equippedId : equippedId === entry.id;
        const isSelected =
          this.selectedCosmeticEntry?.tab === tab && this.selectedCosmeticEntry.id === entry.id;

        const bg = this.scene.add
          .rectangle(slotX, slotY, slotSize, slotSize, 0x1a1a2e, 0.9)
          .setStrokeStyle(2, isEquipped ? hexToPhaser(COLORS.GREEN) : isSelected ? hexToPhaser(COLORS.BLUE) : 0x3b4a6b)
          .setScrollFactor(0)
          .setInteractive({ useHandCursor: true });
        this.productSprites.push(bg as any);
        this.container!.add(bg);
        gridObjs.push(bg);

        if (entry.sprite && this.scene.textures.exists(entry.sprite)) {
          const sprite = this.scene.add.image(slotX, slotY - 6, entry.sprite).setScrollFactor(0);
          sprite.setDisplaySize(slotSize * 0.6, slotSize * 0.6);
          this.productSprites.push(sprite as any);
          this.container!.add(sprite);
          gridObjs.push(sprite);
        } else if (entry.text) {
          const titleText = this.scene.add
            .text(slotX, slotY - 6, entry.text, {
              fontSize: "12px",
              fontFamily: "Anton",
              color: "#fbbf24",
              align: "center",
              wordWrap: { width: slotSize - 8 },
            })
            .setOrigin(0.5)
            .setScrollFactor(0);
          this.productSprites.push(titleText as any);
          this.container!.add(titleText);
          gridObjs.push(titleText);
        }

        // Only equipped tiles get a label below them — an "ACTIVE" badge, matching the
        // skin tab's per-tile ACTIVE/OWNED box. Unequipped tiles get no label at all.
        if (isEquipped) {
          const activeLabel = "ACTIVE";
          const tempText = this.scene.add.text(0, 0, activeLabel, { fontSize: "12px", fontFamily: "Anton" });
          const textWidth = tempText.width;
          const textHeight = tempText.height;
          tempText.destroy();

          const boxPadding = 4;
          const boxWidth = textWidth + boxPadding * 2;
          const boxHeight = textHeight + boxPadding * 2;
          const boxY = slotY + slotSize / 2 + 6;

          const activeBox = this.scene.add.graphics();
          activeBox.fillStyle(hexToPhaser(COLORS.GREEN), 1);
          activeBox.fillRect(slotX - boxWidth / 2, boxY, boxWidth, boxHeight);
          activeBox.lineStyle(2, hexToPhaser(COLORS.BLACK), 1);
          activeBox.strokeRect(slotX - boxWidth / 2, boxY, boxWidth, boxHeight);
          activeBox.setScrollFactor(0);
          this.productSprites.push(activeBox as any);
          this.container!.add(activeBox);
          gridObjs.push(activeBox);

          const activeText = this.scene.add
            .text(slotX, boxY + boxPadding, activeLabel, {
              fontSize: "12px",
              color: COLORS.BLACK,
              fontFamily: "Anton",
              align: "center",
            })
            .setOrigin(0.5, 0)
            .setScrollFactor(0);
          this.productSprites.push(activeText as any);
          this.container!.add(activeText);
          gridObjs.push(activeText);
        }

        if (!isEquipped) {
          bg.on("pointerdown", () => {
            this.selectedCosmeticEntry = isSelected ? null : { tab, id: entry.id };
            renderGrid();
          });
        }
      });

      if (this.selectedCosmeticEntry?.tab === tab) {
        this.showSelectButton(bounds, () => this.equipSelectedCosmetic(tab, equipSlot));
      } else {
        this.hideSelectButton(bounds);
      }
    };

    renderGrid();
  }

  private equipSelectedCosmetic(
    tab: CosmeticTab,
    equipSlot: "profileIcon" | "hatSkin" | "title" | "border",
  ): void {
    const entry = this.selectedCosmeticEntry;
    if (!entry || entry.tab !== tab) return;
    const socket = (window as any).socket;
    const isNone =
      (tab === "hats" && entry.id === NONE_HAT_ID) ||
      (tab === "titles" && entry.id === NONE_TITLE_ID) ||
      (tab === "borders" && entry.id === NONE_BORDER_ID);
    const id = isNone ? null : entry.id;

    socket?.emit("equip-season-cosmetic", { slot: equipSlot, id });
    const onEquipped = (res: { success: boolean; slot: string; id: any }) => {
      socket?.off("season-cosmetic-equipped", onEquipped);
      if (res.success) {
        // Keep window.player.data in sync so the player sprite (base pedestal,
        // market, and match instance) picks up the newly equipped hat.
        const player = (window as any).player;
        if (player?.data && res.slot === "hatSkin") {
          player.data.equippedHat = (res.id as number) ?? undefined;
          player.updateHatSprite?.();
        }
        this.notificationHandler.show(isNone ? "Removed!" : "Equipped!", "success", 1500);
        this.selectedCosmeticEntry = null;
        this.show();
      }
    };
    socket?.on("season-cosmetic-equipped", onEquipped);
  }

  hide(triggerClose: boolean = true) {
    const wasVisible = this.visible;
    this.hideBuyButton();
    this.hideChangeButton();
    this.hideSelectButton();
    this.hideCloseButton();
    this.notificationHandler.hideAll();

    // Destroy all product sprites
    if (this.productSprites) {
      this.productSprites.forEach((sprite) => {
        if (sprite && sprite.scene) {
          // Only destroy if still in scene
          sprite.destroy();
        }
      });
      this.productSprites = [];
    }

    // Destroy all slot rectangles and their associated data
    if (this.slots) {
      this.slots.forEach((slot) => {
        if (slot.rect && slot.rect.scene) {
          // Destroy the rectangle itself
          slot.rect.destroy();
        }
        // Clear slot reference to ensure no lingering objects
        slot.rect = null;
      });
      this.slots = [];
    }

    this.selectedProductIdx = null;
    this.selectedCosmeticEntry = null;
    super.hide();
    if (triggerClose && wasVisible && this.onCloseCallback) {
      this.onCloseCallback();
    }
  }

  private showRightSideSpinner() {
    // Hide right side spinner if it already exists
    this.hideRightSideSpinner();

    const bounds = this.getWindowBounds();
    if (!bounds) return;

    const { x, y, width, height } = bounds;
    const centerX = x + width * 0.75;
    const centerY = y + this.CONTENT_PADDING_Y + 80;

    // Hide all right-side content
    this.rightSideContentElements.forEach((element) => {
      (element as any).setVisible(false);
    });

    // Spinning logo at preview sprite location
    const spinnerSize = 64;
    const spinner = this.scene.add.image(centerX, centerY, "logo-portal");
    spinner.setDisplaySize(spinnerSize, spinnerSize);
    spinner.setScrollFactor(0);

    // Add rotation tween for spinning effect
    this.scene.tweens.add({
      targets: spinner,
      angle: 360,
      duration: 2000,
      repeat: -1,
      ease: "Linear",
    });

    // Create container with just the spinner
    this.rightSideSpinnerContainer = this.scene.add.container(0, 0, [spinner]);
    this.rightSideSpinnerContainer.setDepth(100002);
    this.rightSideSpinnerContainer.setVisible(true);

  }

  private hideRightSideSpinner() {
    // Show all right-side content again
    this.rightSideContentElements.forEach((element) => {
      if (element && element.scene) {
        (element as any).setVisible(true);
      }
    });

    // Destroy spinner container
    if (this.rightSideSpinnerContainer) {
      this.rightSideSpinnerContainer.destroy();
      this.rightSideSpinnerContainer = null;
    }
  }

  // Small, non-blocking indicator shown while checking Apple/Google for purchases
  // we might have missed - unlike showRightSideSpinner, this never hides the shop.
  private showSyncSpinner() {
    this.hideSyncSpinner();

    const bounds = this.getWindowBounds();
    if (!bounds) return;
    const { x, y } = bounds;

    const spinnerSize = 18;
    const spinner = this.scene.add.image(
      x + this.CONTENT_PADDING_X + spinnerSize / 2,
      y + 16,
      "logo-portal",
    );
    spinner.setDisplaySize(spinnerSize, spinnerSize);
    spinner.setScrollFactor(0);

    this.scene.tweens.add({
      targets: spinner,
      angle: 360,
      duration: 1000,
      repeat: -1,
      ease: "Linear",
    });

    const label = this.scene.add
      .text(
        x + this.CONTENT_PADDING_X + spinnerSize + 8,
        y + 16,
        i18next.t("ui:instructions.checkingAppStore") || "Checking with App Store...",
        {
          fontSize: "12px",
          color: COLORS.GRAY,
          fontFamily: "Anton",
        },
      )
      .setOrigin(0, 0.5);
    label.setScrollFactor(0);

    this.syncSpinnerContainer = this.scene.add.container(0, 0, [spinner, label]);
    this.syncSpinnerContainer.setDepth(100003);
  }

  private hideSyncSpinner() {
    if (this.syncSpinnerContainer) {
      this.syncSpinnerContainer.destroy();
      this.syncSpinnerContainer = null;
    }
  }

  // Silent background reconciliation - runs once per show() so a purchase Apple/Google
  // charged for but our server never recorded gets backfilled the next time the shop
  // is opened, without the user having to do anything.
  private async performBackgroundSync() {
    if (!isTouchDevice() || !this.iapManager.isAvailable()) return;
    if (!(window as any).socket) return;

    this.showSyncSpinner();
    const result = await this.iapManager.syncPurchases();
    this.hideSyncSpinner();

    if (!this.visible) return; // window was closed while syncing

    if (result.granted > 0 || result.revoked > 0) {
      console.info("[CosmeticsWindow] Background sync found changes, refreshing shop:", result);
      await this.show({ skipBackgroundSync: true });
    }
  }

  private async handleRestoreButton() {
    this.showSyncSpinner();
    const result = await this.iapManager.syncPurchases({ heavy: true });
    this.hideSyncSpinner();

    if (result.granted > 0 || result.revoked > 0) {
      this.notificationHandler.show(
        i18next.t("ui:instructions.purchasesRestored", { count: result.granted }) ||
          `✓ ${result.granted} purchase(s) restored`,
        "success",
        3000,
      );
      if (this.visible) await this.show({ skipBackgroundSync: true });
    } else {
      this.notificationHandler.show(
        i18next.t("ui:instructions.everythingUpToDate") || "Everything up to date",
        "success",
        3000,
      );
    }
  }
}
